import { getAuthToken, useSession } from '@shared/lib/auth/session-store'
import { createRequestId } from './request-id'
import { toAppError } from './error'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  recaptchaToken?: string
}

export type UploadOptions = Omit<RequestOptions, 'body'> & {
  onProgress?: (progress: number) => void
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, recaptchaToken, ...rest } = options
  const requestHeaders = new Headers(headers)
  requestHeaders.set('x-request-id', createRequestId())
  if (recaptchaToken) {
    requestHeaders.set('x-recaptcha-token', recaptchaToken)
  }

  const token = getAuthToken()
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  let payload: BodyInit | undefined
  if (body !== undefined) {
    if (body instanceof FormData) {
      payload = body
    } else {
      requestHeaders.set('Content-Type', 'application/json')
      payload = JSON.stringify(body)
    }
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: requestHeaders,
    body: payload,
  })

  if (response.status === 204) {
    return undefined as T
  }

  const data: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const error = toAppError(response.status, data)
    if (error.isUnauthorized) {
      useSession.getState().clear()
    }
    throw error
  }

  return data as T
}

export async function apiFetchBlob(path: string): Promise<Blob> {
  const requestHeaders = new Headers()
  requestHeaders.set('x-request-id', createRequestId())
  const token = getAuthToken()
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: requestHeaders,
    cache: 'no-store',
  })
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null)
    const error = toAppError(response.status, data)
    if (error.isUnauthorized) {
      useSession.getState().clear()
    }
    throw error
  }
  return response.blob()
}

export async function apiUpload<T>(
  path: string,
  formData: FormData,
  options: UploadOptions = {},
): Promise<T> {
  if (options.onProgress) {
    return apiUploadWithProgress<T>(path, formData, options)
  }

  return apiFetch<T>(path, {
    ...options,
    method: options.method ?? 'POST',
    body: formData,
  })
}

async function apiUploadWithProgress<T>(
  path: string,
  formData: FormData,
  options: UploadOptions,
): Promise<T> {
  const { headers, recaptchaToken, method, onProgress, signal } = options
  const requestHeaders = new Headers(headers)
  requestHeaders.set('x-request-id', createRequestId())
  if (recaptchaToken) {
    requestHeaders.set('x-recaptcha-token', recaptchaToken)
  }

  const token = getAuthToken()
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method ?? 'POST', `${BASE_URL}${path}`)
    requestHeaders.forEach((value, key) => xhr.setRequestHeader(key, value))

    const abort = () => {
      xhr.abort()
      reject(new DOMException('Upload cancelado', 'AbortError'))
    }

    if (signal?.aborted) {
      abort()
      return
    }

    signal?.addEventListener('abort', abort, { once: true })

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(Math.round((event.loaded / event.total) * 100))
      }
    }

    xhr.onload = () => {
      signal?.removeEventListener('abort', abort)
      const data: unknown = parseJson(xhr.responseText)
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.status === 204 ? (undefined as T) : (data as T))
        return
      }

      const error = toAppError(xhr.status, data)
      if (error.isUnauthorized) {
        useSession.getState().clear()
      }
      reject(error)
    }

    xhr.onerror = () => {
      signal?.removeEventListener('abort', abort)
      reject(new Error('Nao foi possivel enviar o arquivo.'))
    }

    xhr.onabort = () => {
      signal?.removeEventListener('abort', abort)
    }

    xhr.send(formData)
  })
}

function parseJson(text: string): unknown {
  if (!text) {
    return null
  }
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}
